import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { ConfirmationModalComponent } from '@app/shared/components/confirmation-modal/confirmation-modal.component';
import { ToolRequestsService } from '@app/core/services/tool-requests.service';
import { ToolRequest } from '@app/shared/models/toolRequests.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Log } from 'ng2-logger';
import { Section } from '@models/enums/section.utils';
import { NotificationService } from '@services/notification.service';
import { Subscription } from 'rxjs';
import { AppUtils } from '@app/shared/util/app.utils';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ResolutionService } from "@services/resolution.service";
import { ToolProcessingEntry } from "@components/tool-card/tool-processing-card.model";
import { TranslateService } from "@ngx-translate/core";
import { ToolProcessingCardComponent } from "@components/tool-card/tool-processing-card.component";
import {
    ToolProcessingFilterBottomSheetComponent
} from "@app/roles/kardex-operator/components/tool-processing-bottom-sheet/tool-processing-filter-bottom-sheet.component";
import { ToolRequestStatus } from "@app/tool/model/enums/tool-request-status.enum";
import { Line } from "@models/line.model";
import * as _ from "lodash";

@UntilDestroy()
@Component({
    selector: 'app-incoming-tool-requests',
    templateUrl: './incoming-tool-requests.component.html',
    styleUrls: ['./incoming-tool-requests.component.scss']
})
export class IncomingToolRequestsComponent implements OnInit, OnDestroy {
    _logger = Log.create(IncomingToolRequestsComponent.name);

    @ViewChild(ConfirmationModalComponent, {static: true}) confirmationModal: ConfirmationModalComponent;
    @ViewChild(ToolProcessingFilterBottomSheetComponent, {static: false}) filterSheet: ToolProcessingFilterBottomSheetComponent;

    tableConfig = new TableConfig();
    isLoading = false;

    requests: Array<ToolRequest> = [];
    // Used on mobile
    toolProcessingEntries: Array<ToolProcessingEntry> = [];

    selectedToolRequest: ToolRequest;

    type: string;
    selectedPlantsChangedSubscription: Subscription;

    availableLines: any[] = [];
    availableStatuses = [
        'All',
        ToolRequestStatus.APPROVED,
        ToolRequestStatus.WAITING_FOR_PROCESSING
    ];

    // TODO left empty until we can clarify if we want to filter by types accordingly
    availableTypes = [];

    @HostBinding('class.mobile') isMobile = false;

    constructor(private toolRequestsService: ToolRequestsService,
                private router: Router,
                private route: ActivatedRoute,
                private resolutionService: ResolutionService,
                private notificationService: NotificationService,
                private translate: TranslateService) {
        this.selectedPlantsChangedSubscription = this.notificationService.selectedPlantsChangedAsObservable.subscribe(data => {
            this.getIncomingToolRequest(this.tableConfig);
        });

        this.resolutionService.screenSize$.pipe(untilDestroyed(this)).subscribe(screen => {
            this.isMobile = screen.mobile;
        })
    }

    ngOnInit() {
        if (this.route.snapshot.routeConfig.path === 'lines') {
            this.type = 'LINE';
        } else if (this.route.snapshot.routeConfig.path === 'facilities') {
            this.type = 'TICKET';
        }
        this.isLoading = true;
        this.getIncomingToolRequest(this.tableConfig);
    }

    ngOnDestroy(): void {
        if (this.selectedPlantsChangedSubscription) {
            this.selectedPlantsChangedSubscription.unsubscribe();
        }
    }

    getIncomingToolRequest(tableConfig = this.tableConfig) {
        tableConfig = AppUtils.appendPlantsQuery(tableConfig);
        this._logger.info("getIncomingToolRequest:: Retrieving list of tool requests", this.tableConfig);
        this.toolRequestsService.getAllToolRequests(tableConfig, this.type, Section.ProcessToolRequests).subscribe(
            data => {
                this._logger.info("getIncomingToolRequest:: API returned data", data);
                this.requests = data.items;
                if (!this.areLinesComputed) {
                    this.availableLines = this.computeLines();
                }

                this.toolProcessingEntries = this.computeProcessingEntries(this.requests);
                this.tableConfig.pager.count = data.total;
                this.isLoading = false;
            },
            error => {
                this._logger.error("getIncomingToolRequest:: API returned error", error);
                this.isLoading = false;
            }
        );
    }


    private computeLines() {
        const uniqueLines = _.uniqBy(this.requests.map(request => request.line), (request) => {
            return request.id;
        });

        return [{id: null, name: 'All'}, ...uniqueLines];
    }

    private computeProcessingEntries(toolProcessingRequests: Array<ToolRequest>) {
        return toolProcessingRequests.map(request => ToolProcessingCardComponent.fromToolRequest(request));
    }

    onSortChange(tableConfig: TableConfig) {
        this.isLoading = true;
        this.getIncomingToolRequest(tableConfig);
    }

    onFilter(tableConfig: TableConfig) {
        this.isLoading = true;
        this.getIncomingToolRequest(tableConfig);
    }

    onPageChange(tableConfig: TableConfig) {
        this.isLoading = true;
        this.getIncomingToolRequest(tableConfig);
    }

    onProcess(toolRequest: ToolRequest) {
        this._logger.info("onProcess:: Processing tool request", toolRequest);
        this.selectedToolRequest = toolRequest;
        this.toolRequestsService.startProcessingById(this.selectedToolRequest.id).subscribe(
            data => {
                this._logger.info("onProcess:: API returned data", data);
                this.router.navigate([this.selectedToolRequest.id], {relativeTo: this.route}).then();
            },
            error => {
                this._logger.error("onProcess:: API returned error", error);
            }
        )
    }

    onResumeProcess(toolRequest: ToolRequest) {
        this._logger.info("onResumeProcess:: Resuming processing for tool request", toolRequest);
        this.selectedToolRequest = toolRequest;
        this.router.navigate([this.selectedToolRequest.id], {relativeTo: this.route});
    }

    onSelect(entry: ToolProcessingEntry) {
        this._logger.info("onSelect:: Processing tool request", entry?.title);
        if ((entry.payload as ToolRequest).status === 'WaitingForProcessing') {
            this.onResumeProcess(entry.payload);
        } else {
            this.onProcess(entry.payload);
        }
    }

    onApplyFilters(config: TableConfig) {
        this.tableConfig = config;
        this.onFilter(this.tableConfig);
        this.filterSheet.close();
    }

    get title() {
        return this.type && this.type === 'LINE' ?
            'nav.sections.process-tool-requests.items.processLines' :
            'nav.sections.process-tool-requests.items.processFacilities';
    }

    get areLinesComputed() {
        return this.availableLines && this.availableLines.length > 0;
    }

    openFilterSheet() {
        this.filterSheet.open();
    }
}
